/*
 * routine-results-v2.js
 * /pages/skin-type-results — the Aug 2026 redesign.
 *
 * Replaces the sidebar + accordion + Slick page. The model comes from the
 * Claude Design handoff and Jonathan's call of 11 Aug:
 *
 *   - ONE flat list of steps. No AM/PM toggle — "the AM, PM here at the top and
 *     then the day and night thing... it's a little bit confusing" (0:35).
 *     Time of day is a badge on the step instead.
 *   - Every step arrives ANSWERED, pre-selected with the EBC Choice. The row of
 *     thumbnails swaps the product; the panel above it re-renders.
 *   - Essential / Advanced rather than three tiers (10:24).
 *   - Steps stay open so the next one is always visible (11:30).
 *   - A step can be removed, behind a confirm that says what it costs (19:03).
 *
 * WHAT THIS FILE DOES NOT OWN: money formatting, which stays in
 * skin-experience-logic.js, shared with the routine builder. There is no
 * automatic bundle discount any more (Bundle20 was retired): the page quotes the
 * plain subtotal, and any offer is stated by the merchant-edited offer callout.
 */
(function () {
  'use strict';

  var STORAGE = {
    PROFILE: 'skinQuizProfile',
    SELECTED: 'skinQuizSelectedProducts',
    TIER: 'ebcRoutineTier',
    REMOVED: 'ebcRoutineRemovedSteps',
    ONECLEANSER: 'ebcRoutineOneCleanser'
  };

  var VALID_SKIN_TYPES = ['Dry', 'Oily', 'Combination', 'Normal'];
  var VALID_SENSITIVITY = ['Sensitive', 'Not Sensitive'];

  // ---------------------------------------------------------------
  // Step model.
  //
  // Driven by the REAL routine metaobject fields — nothing here invents a
  // product. `field` names resolve against the routine's `products` object; the
  // ones that depend on the shopper's answers are resolved in resolveField().
  //
  // Essential is the four steps Jonathan called the core (cleanse, hydrate,
  // protect, repair). Advanced adds the treatment serum and the eye cream —
  // "the advanced would be like adding the plated at night" (11:00).
  //
  // ORDER IS THE ORDER YOU APPLY THEM IN — thinnest to thickest, sunscreen
  // last: cleanse, eyes, treat, repair, hydrate, protect. This list used to run
  // hydrate before repair, which told people to put retinol on top of their
  // moisturiser and put SPF after a PM-only step. The visible "Step 1..N"
  // numbering is generated from this array, so the array IS the instructions.
  // ---------------------------------------------------------------
  var STEP_DEFS = [
    {
      id: 'cleanse', pill: 'Cleanse', tier: 'essential',
      time: 'AM + PM', icons: '☀ ☾',
      field: 'am_cleanser', alsoField: 'pm_cleanser',
      category: 'Cleanser',
      why: 'Everything else lands better on skin that has been cleaned without being stripped. One bottle covers morning and night.',
      removedNote: 'You will cleanse with whatever you use today.'
    },
    {
      id: 'eyes', pill: 'Eyes', tier: 'advanced',
      time: 'AM + PM', icons: '☀ ☾',
      field: 'eye_cream',
      category: 'Eye cream',
      why: 'The skin around the eyes is the thinnest on the face and usually shows change first. A dedicated formula is gentler than taking your moisturiser up there.',
      removedNote: 'Part of Advanced — add it back any time.'
    },
    {
      id: 'treat', pill: 'Treat', tier: 'advanced',
      time: 'PM', icons: '☾',
      field: 'treatment',
      category: 'Treatment serum',
      why: 'The step that does the heavy lifting on your main concern. Night is when your skin is repairing anyway, so this is where it goes.',
      removedNote: 'Part of Advanced — add it back any time.'
    },
    {
      id: 'repair', pill: 'Repair', tier: 'essential',
      time: 'PM', icons: '☾',
      field: 'retinol',
      category: 'Retinol',
      why: 'Retinoids are the most evidenced thing you can put on fine lines. Start two nights a week for the first month, then three.',
      removedNote: 'Fine lines respond slowly without a retinoid.'
    },
    {
      id: 'hydrate', pill: 'Hydrate', tier: 'essential',
      time: 'AM + PM', icons: '☀ ☾',
      field: 'moisturizer',
      category: 'Moisturizer',
      why: 'Barrier support is what keeps the active steps comfortable. Skip it and everything else stings more than it should.',
      removedNote: 'Actives are harsher without a moisturiser over them.'
    },
    {
      id: 'protect', pill: 'Protect', tier: 'essential',
      time: 'AM', icons: '☀',
      field: 'sunscreen',
      category: 'Broad-spectrum SPF',
      why: 'The highest-value step in any routine. Every other step works better behind daily SPF, and undoing sun damage is far slower than preventing it.',
      removedNote: 'This is the step we would keep above all others.'
    }
  ];

  var state = {
    routine: null,
    profile: null,
    steps: [],          // { def, slots[], splitAvailable, removed, whyOpen }
    oneCleanser: false, // shopper asked to buy one cleanser instead of two
    tier: 'essential',
    detailStepId: null,
    pendingRemoveId: null
  };

  // ---------------------------------------------------------------
  // Small helpers
  // ---------------------------------------------------------------
  function $(id) { return document.getElementById(id); }

  function esc(t) {
    if (t == null) return '';
    var d = document.createElement('div');
    d.textContent = String(t);
    return d.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Liquid's strip_html leaves entities behind, so decode once before escaping
  // or "&amp;" prints literally on the card.
  function decode(t) {
    if (t == null) return '';
    var s = String(t);
    if (s.indexOf('&') === -1) return s;
    var ta = document.createElement('textarea');
    ta.innerHTML = s;
    return ta.value;
  }

  function money(cents) {
    return window.SkinExp.formatMoneyFromCents(cents);
  }

  function readJSON(key, fallback) {
    try {
      var raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) { return fallback; }
  }

  function writeJSON(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) {}
  }

  // #rv2Steps used to be the live region itself, and renderSteps() replaces its
  // entire innerHTML — so every swatch tap re-announced all four to six steps,
  // headings, descriptions, prices and option buttons included. Nobody can hear
  // past that. The container is inert now and this says the one thing that
  // actually changed.
  function announce(message) {
    var el = $('rv2Live');
    if (!el || !message) return;
    // Same string twice in a row is not re-read unless the node is cleared.
    el.textContent = '';
    window.setTimeout(function () { el.textContent = message; }, 40);
  }

  // ---------------------------------------------------------------
  // Profile + routine matching. Ported from section-skin-quiz-new.js so the
  // shopper lands on exactly the routine they would have before — this redesign
  // changes the layout, not who gets recommended what.
  // ---------------------------------------------------------------
  function loadProfile() {
    var data = readJSON(STORAGE.PROFILE, null);
    if (!data || typeof data !== 'object') return null;
    if (!data.skinType || VALID_SKIN_TYPES.indexOf(data.skinType) === -1) return null;
    if (!data.sensitivity || VALID_SENSITIVITY.indexOf(data.sensitivity) === -1) {
      data.sensitivity = 'Not Sensitive';
    }
    return data;
  }

  function fieldMatches(fieldValue, searchValue) {
    if (Array.isArray(fieldValue)) return fieldValue.indexOf(searchValue) !== -1;
    return fieldValue === searchValue;
  }

  function pickRoutine(routines, profile) {
    var i;
    for (i = 0; i < routines.length; i++) {
      if (fieldMatches(routines[i].skin_type, profile.skinType) &&
          fieldMatches(routines[i].sensitivity, profile.sensitivity) &&
          fieldMatches(routines[i].concern, profile.primaryConcern)) {
        return routines[i];
      }
    }
    for (i = 0; i < routines.length; i++) {
      if (fieldMatches(routines[i].skin_type, profile.skinType)) return routines[i];
    }
    return routines[0] || null;
  }

  // Which metaobject field a step reads, once the shopper's answers are known.
  function resolveField(def, profile) {
    if (def.field === 'treatment') {
      return profile.primaryConcern === 'Hyperpigmentation'
        ? 'pigment_treatment'
        : 'non_pigment_treatment';
    }
    if (def.field === 'retinol') {
      // Pregnancy swaps retinol for the pregnancy-safe alternative.
      return profile.isPregnant ? 'retinol_alternative' : 'retinol';
    }
    if (def.field === 'sunscreen') {
      var p = profile.sunscreenPrefs || {};
      var type = p.type || 'NoPreference';
      var color = p.color === 'Tinted' ? 'tinted' : 'untinted';
      if (type === 'Physical') return 'physical_' + color;
      if (type === 'Chemical') return 'chemical_' + color;
      return 'no_preference_' + color;
    }
    return def.field;
  }

  // The category line under the pill. It has to follow resolveField(), not just
  // print def.category: a pregnant shopper is shown the retinol-free product, so
  // labelling that card "Retinol" tells her the one thing she is avoiding.
  function categoryFor(def, profile) {
    if (def.field === 'retinol' && profile && profile.isPregnant) return 'Retinol alternative';
    return def.category;
  }

  // Options from one named field, de-duplicated.
  function optionsFromFields(routine, fields) {
    var products = routine.products || {};
    var out = [];
    var seen = {};
    fields.forEach(function (f) {
      (products[f] || []).forEach(function (p) {
        if (!p || !p.id || seen[p.id]) return;
        seen[p.id] = true;
        out.push(p);
      });
    });
    return out;
  }

  function optionsFor(routine, def, profile) {
    var fields = [resolveField(def, profile)];
    if (def.alsoField) fields.push(def.alsoField);
    return optionsFromFields(routine, fields);
  }

  // ---------------------------------------------------------------
  // Slots.
  //
  // A step used to be one product. It is one product OR TWO, because the
  // routine records keep separate morning and evening cleansers and in 16 of
  // the 20 routines those lists share nothing at all. Merging them, as this
  // file did, meant a shopper could be handed an evening-only cleanser as their
  // morning step with no warning.
  //
  // Only the cleanse step splits today. That is a deliberate scope choice, not
  // a limitation of the shape: any step given an `amField`/`pmField` pair would
  // work the same way, but no other step has that data and nobody has asked for
  // it, so nothing else is speculatively wired up.
  function slotsFor(routine, def, profile) {
    var single = function () {
      return [{ key: 'one', label: null, options: optionsFor(routine, def, profile), selectedId: null }];
    };
    if (def.id !== 'cleanse' || !def.alsoField) return single();

    var amField = resolveField(def, profile);
    var am = optionsFromFields(routine, [amField]);
    var pm = optionsFromFields(routine, [def.alsoField]);
    if (!am.length || !pm.length) return single();

    // Same list on both sides? Then it genuinely is one product used twice, and
    // splitting the card would invent a distinction the routine did not make.
    var ids = function (l) { return l.map(function (x) { return String(x.id); }).sort().join(','); };
    if (ids(am) === ids(pm)) return single();

    return [
      { key: 'am', label: 'Morning', options: am, selectedId: null },
      { key: 'pm', label: 'Evening', options: pm, selectedId: null }
    ];
  }

  // Every chosen product in a step, one per slot. Two cleansers is two lines.
  function stepProducts(step) {
    var out = [];
    (step.slots || []).forEach(function (sl) {
      var p = slotProduct(sl);
      if (p) out.push(p);
    });
    return out;
  }

  function slotProduct(slot) {
    for (var i = 0; i < slot.options.length; i++) {
      if (String(slot.options[i].id) === String(slot.selectedId)) return slot.options[i];
    }
    return slot.options[0] || null;
  }

  // True when the shopper has asked to buy one cleanser rather than two.
  function isCollapsed(step) {
    return step.def.id === 'cleanse' && state.oneCleanser === true;
  }

  // The slots actually shown and charged for. Collapsing keeps the morning
  // slot, because that is the list a single cleanser is chosen from.
  function activeSlots(step) {
    if (isCollapsed(step) && step.slots.length > 1) return [step.slots[0]];
    return step.slots;
  }

  // ---------------------------------------------------------------
  // Build state
  // ---------------------------------------------------------------
  function buildSteps() {
    var savedRemoved = readJSON(STORAGE.REMOVED, []) || [];
    var savedSelected = readJSON(STORAGE.SELECTED, {}) || {};

    state.oneCleanser = readJSON(STORAGE.ONECLEANSER, false) === true;

    state.steps = STEP_DEFS.map(function (def) {
      var slots = slotsFor(state.routine, def, state.profile);
      var savedForStep = savedSelected[def.id];
      // Honour a saved pick, BY STEP. This used to be stored as a set of variant
      // ids and restored by asking "is any saved id an option here?" - so a
      // product offered by two steps was re-selected in both. That is not
      // hypothetical: nine routine/step pairs share a serum between Treat and
      // Repair today (SkinMedica TNS Advanced+, Neocutis BIO SERUM FIRM, Plated
      // DAILY Serum), so a shopper who picked one for Treat came back to find it
      // sitting in Repair as well.
      //
      // Keys are step ids now. Old variant-keyed data simply does not match any
      // step id, so it is ignored and the next save rewrites it - no migration.
      slots.forEach(function (sl) {
        // EBC Choice is the first option the merchandising team put in the field.
        sl.selectedId = sl.options.length ? sl.options[0].id : null;
        // A saved pick is keyed by step AND slot, so a product offered by two
        // steps cannot be restored into both. Anything that no longer matches
        // an option here is ignored and rewritten on the next save.
        var saved = savedForStep && typeof savedForStep === 'object' ? savedForStep[sl.key] : null;
        if (saved == null) return;
        for (var i = 0; i < sl.options.length; i++) {
          if (String(sl.options[i].id) === String(saved)) { sl.selectedId = sl.options[i].id; break; }
        }
      });
      return {
        def: def,
        slots: slots,
        splitAvailable: slots.length > 1,
        removed: savedRemoved.indexOf(def.id) !== -1,
        whyOpen: false
      };
    }).filter(function (s) { return s.slots.some(function (sl) { return sl.options.length > 0; }); });

    var savedTier = null;
    try { savedTier = localStorage.getItem(STORAGE.TIER); } catch (e) {}
    state.tier = savedTier === 'advanced' ? 'advanced' : 'essential';
  }

  function stepById(id) {
    for (var i = 0; i < state.steps.length; i++) {
      if (state.steps[i].def.id === id) return state.steps[i];
    }
    return null;
  }

  function stepInTier(step) {
    return state.tier === 'advanced' || step.def.tier === 'essential';
  }

  // A step counts toward the money only if it is in the chosen tier, has not
  // been removed, and actually has something selected.
  function activeSteps() {
    return state.steps.filter(function (s) {
      return stepInTier(s) && !s.removed && stepProducts(s).length > 0;
    });
  }

  // The step's primary product - the first slot. Used where one representative
  // product is wanted (the detail sheet, the card heading).
  function selectedProduct(step) {
    return slotProduct(activeSlots(step)[0] || step.slots[0]);
  }

  // One line per DISTINCT product, not per step.
  //
  // Nine routine/step pairs offer the same serum under both Treat and Repair, so
  // a shopper can legitimately end up with one product answering two steps. This
  // summed it once per step while addRoutineToCart() adds it once per product -
  // the page quoted more than the cart charged, which is the exact promise the
  // rest of this file exists to keep. Counting distinct products makes the two
  // agree, and matches what Shopify will actually bill.
  function totals() {
    var seen = {};
    var items = [];
    activeSteps().forEach(function (s) {
      activeSlots(s).forEach(function (sl) {
        var p = slotProduct(sl);
        if (!p || seen[p.id]) return;
        seen[p.id] = true;
        items.push(p.price);
      });
    });
    return { originalCents: window.SkinExp.sumCents(items) };
  }

  // ---------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------
  function starsHTML(rating) {
    var pct = Math.max(0, Math.min(100, (Number(rating) || 0) / 5 * 100));
    return '<span class="rv2-stars" aria-hidden="true">★★★★★' +
           '<span class="rv2-stars__on" style="width:' + pct + '%">★★★★★</span></span>';
  }

  function ratingRow(p) {
    var count = parseInt(p.rating_count, 10) || 0;
    if (!count) return '';   // no reviews: say nothing rather than show five empty stars
    var r = Number(p.rating) || 0;
    return '<div class="rv2-rating">' + starsHTML(r) +
      (r ? '<span class="rv2-rating__num">' + r.toFixed(1) + '</span>' : '') +
      '<span class="rv2-rating__count">(' + count + ')</span></div>';
  }

  function optionHTML(step, slot, p, index) {
    var selected = String(p.id) === String(slot.selectedId);
    var img = p.image ? 'background-image:url(' + esc(p.image) + ')' : '';
    return '' +
      '<button type="button" class="rv2-opt' + (selected ? ' is-selected' : '') + '"' +
        ' data-step="' + esc(step.def.id) + '" data-slot="' + esc(slot.key) + '" data-variant="' + esc(p.id) + '"' +
        ' aria-pressed="' + (selected ? 'true' : 'false') + '">' +
        '<span class="rv2-opt__img" style="' + img + '" role="img" aria-label="' + esc(decode(p.name)) + '"></span>' +
        '<span class="rv2-opt__label">' + esc(decode(p.brand || p.name)) + '</span>' +
        '<span class="rv2-opt__price">' + money(p.price) + '</span>' +
        (selected ? '<span class="rv2-opt__tick" aria-hidden="true">✓</span>' : '') +
        (index === 0 ? '<span class="rv2-opt__choice">EBC Choice</span>' : '') +
      '</button>';
  }

  function stepHTML(step) {
    var d = step.def;
    var inTier = stepInTier(step);
    var num = (state.stepNums || {})[d.id];
    // Removed steps keep their name but lose the number — they are not a step
    // of the routine any more, so numbering them would misdescribe the order.
    var pill = '<span class="rv2-pill' + (num ? '' : ' is-muted') + '">' +
      (num ? '<span class="rv2-pill__n">Step ' + num + '</span>' : '') +
      '<span class="rv2-pill__name">' + esc(d.pill) + '</span>' +
    '</span>';

    // A step that is not in the chosen tier is simply not part of this routine,
    // so it is not drawn at all. It used to render a greyed placeholder reading
    // "In the Advanced routine" with an Add Advanced button, which padded the
    // Essential routine with two dead cards and made a four-step routine look
    // like a six-step one with holes in it. The tier toggle at the top of the
    // page is the way in to those steps.
    if (!inTier) return '';

    if (step.removed) {
      return '<section class="rv2-step is-removed" data-step="' + esc(d.id) + '">' + pill +
        '<div class="rv2-step__ghost">' +
          '<div><p class="rv2-ghost__title">Removed from your routine</p>' +
          '<p class="rv2-ghost__note">' + esc(d.removedNote) + '</p></div>' +
          '<button type="button" class="rv2-ghostbtn" data-action="addback" data-step="' + esc(d.id) + '">Add back</button>' +
        '</div></section>';
    }

    var shown = activeSlots(step);
    if (!stepProducts(step).length) return '';

    // Only the first slot carries the full panel. The second is the same
    // category by definition, so repeating the rating, the blurb and a second
    // Learn more bought nothing and cost 336px on this step.
    var body = shown.map(function (sl, i) { return slotHTML(step, sl, shown.length > 1, i > 0); }).join('');

    // The line that explains why there are two, and the way out of it. Kept to
    // one sentence: it states what the second product is FOR, which is a fact
    // about the bundle, not a claim about skin.
    var splitNote = '';
    if (step.splitAvailable) {
      splitNote = '<div class="rv2-split">' +
        (state.oneCleanser
          ? '<p class="rv2-split__line">One cleanser, morning and night.</p>' +
            '<button type="button" class="rv2-split__btn" data-action="two-cleansers">Use a separate evening cleanser</button>'
          : '<p class="rv2-split__line">Two cleansers: the evening one takes off your sunscreen.</p>' +
            '<button type="button" class="rv2-split__btn" data-action="one-cleanser">Use one cleanser instead</button>') +
      '</div>';
    }

    return '' +
      '<section class="rv2-step" data-step="' + esc(d.id) + '">' + pill +
        '<div class="rv2-card">' +
          body +
          splitNote +
          // The why copy sits INSIDE the foot, directly after the button that
          // reveals it. The foot is a column flex on mobile, so emitting the
          // paragraph after the foot closed put it below "Remove this step" —
          // the disclosure opened and the text appeared under the wrong control.
          '<div class="rv2-card__foot">' +
            '<button type="button" class="rv2-whystep" data-action="why" data-step="' + esc(d.id) + '"' +
              ' aria-expanded="' + (step.whyOpen ? 'true' : 'false') + '" aria-controls="rv2why-' + esc(d.id) + '">' +
              '<span class="rv2-whystep__i" aria-hidden="true">i</span>' +
              '<span class="rv2-whystep__t">Why it works</span>' +
              '<span class="rv2-whystep__c" aria-hidden="true">' + (step.whyOpen ? '▲' : '▼') + '</span>' +
            '</button>' +
            (step.whyOpen ? '<p class="rv2-card__why" id="rv2why-' + esc(d.id) + '">' + esc(d.why) + '</p>' : '') +
            '<button type="button" class="rv2-remove" data-action="remove" data-step="' + esc(d.id) + '">Remove this step</button>' +
          '</div>' +
        '</div>' +
      '</section>';
  }

  // One product panel plus its own swatch row. A step renders one of these, or
  // two when the routine names a different morning and evening product.
  function slotHTML(step, slot, showLabel, compact) {
    var d = step.def;
    var p = slotProduct(slot);
    if (!p) return '';

    var options = slot.options.map(function (o, i) { return optionHTML(step, slot, o, i); }).join('');
    var timeLabel = showLabel ? slot.label : d.time;
    var icons = showLabel ? (slot.key === 'am' ? '\u2600' : '\u263e') : d.icons;

    // In compact mode the NAME is the detail trigger. The Learn more row goes;
    // the ability to read about a product you are buying does not.
    var nameHTML = compact
      ? '<button type="button" class="rv2-card__name rv2-card__name--btn" data-action="detail"' +
          ' data-step="' + esc(d.id) + '" data-slot="' + esc(slot.key) + '">' + esc(decode(p.name)) + '</button>'
      : '<h2 class="rv2-card__name">' + esc(decode(p.name)) + '</h2>';

    return '' +
      '<div class="rv2-card__grid' + (showLabel ? ' is-slot' : '') + (compact ? ' is-compact' : '') + '" data-slot="' + esc(slot.key) + '">' +
        '<div class="rv2-card__info">' +
          (showLabel
            ? '<p class="rv2-slot__label"><span class="rv2-slot__when">' + esc(slot.label) + '</span></p>'
            : '<p class="rv2-card__cat">' + esc(categoryFor(d, state.profile)) + '</p>') +
          nameHTML +
          (compact ? '' : ratingRow(p)) +
          (!compact && p.description ? '<p class="rv2-card__desc">' + esc(decode(p.description)) + '</p>' : '') +
          '<div class="rv2-card__meta">' +
            '<span class="rv2-card__price">' + money(p.price) +
              (p.size ? '<span class="rv2-card__size">' + esc(p.size) + '</span>' : '') + '</span>' +
            '<span class="rv2-card__time">' + esc(timeLabel) +
              '<span class="rv2-card__icons" aria-hidden="true">' + icons + '</span></span>' +
          '</div>' +
          (compact ? '' :
            '<button type="button" class="rv2-learn" data-action="detail" data-step="' + esc(d.id) + '" data-slot="' + esc(slot.key) + '">' +
            '<span>Learn more about this product</span><span aria-hidden="true">\u2192</span></button>') +
        '</div>' +
        '<div class="rv2-card__picker">' +
          '<p class="rv2-picker__label">' + slot.options.length + ' option' + (slot.options.length === 1 ? '' : 's') +
            '<span class="rv2-picker__hint">tap to swap</span></p>' +
          '<div class="rv2-opts">' + options + '</div>' +
        '</div>' +
      '</div>';
  }

  // "Step 1..N" numbers the steps the shopper ACTUALLY performs, in application
  // order. Steps that are out of tier or removed are not part of the routine, so
  // they get no number rather than a number the shopper is told to skip — and
  // removing a step renumbers the rest, which is the honest thing to show
  // someone following it in the bathroom.
  function stepNumbers() {
    var map = {};
    var n = 0;
    state.steps.forEach(function (s) {
      if (!stepInTier(s) || s.removed) return;
      map[s.def.id] = ++n;
    });
    return map;
  }

  // Identifies a control well enough to find its replacement after the wipe.
  function controlKey(el) {
    if (!el || !el.getAttribute) return null;
    return [
      el.getAttribute('data-action') || 'opt',
      el.getAttribute('data-step') || '',
      el.getAttribute('data-variant') || ''
    ].join('|');
  }

  // Rebuilding #rv2Steps with innerHTML throws away two things the shopper
  // cares about, and both were being lost on EVERY tap:
  //
  //   1. Focus. The button that was just activated stops existing, so focus
  //      falls back to <body>. Someone on a keyboard or a switch had to tab
  //      from the top of the page again after swapping a single product.
  //   2. The horizontal scroll offset of .rv2-opts. Only about three of the
  //      118px swatches fit on a phone, so choosing the fourth one scrolled the
  //      row back to the start and hid the product they had just picked.
  //
  // Both are captured before the wipe and restored after it.
  function renderSteps() {
    var host = $('rv2Steps');
    if (!host) return;

    var active = document.activeElement;
    var wantKey = (active && host.contains(active)) ? controlKey(active) : null;

    var offsets = {};
    host.querySelectorAll('.rv2-step[data-step]').forEach(function (sec) {
      var row = sec.querySelector('.rv2-opts');
      if (row && row.scrollLeft) offsets[sec.getAttribute('data-step')] = row.scrollLeft;
    });

    state.stepNums = stepNumbers();
    host.innerHTML = state.steps.map(stepHTML).join('');

    Object.keys(offsets).forEach(function (id) {
      var sec = host.querySelector('.rv2-step[data-step="' + id + '"]');
      var row = sec && sec.querySelector('.rv2-opts');
      if (row) row.scrollLeft = offsets[id];
    });

    if (!wantKey) return;
    var candidates = host.querySelectorAll('button');
    for (var i = 0; i < candidates.length; i++) {
      if (controlKey(candidates[i]) === wantKey) {
        // preventScroll: the element is already where the shopper is looking;
        // letting the browser re-scroll to it jumps the page.
        try { candidates[i].focus({ preventScroll: true }); } catch (e) { candidates[i].focus(); }
        return;
      }
    }
  }

  function renderTiers() {
    var counts = { essential: 0, advanced: 0 };
    state.steps.forEach(function (s) {
      if (s.def.tier === 'essential') { counts.essential++; counts.advanced++; }
      else { counts.advanced++; }
    });
    document.querySelectorAll('[data-tier-count]').forEach(function (el) {
      var t = el.getAttribute('data-tier-count');
      el.textContent = counts[t] + ' steps';
    });
    document.querySelectorAll('.rv2-tier').forEach(function (btn) {
      var on = btn.getAttribute('data-tier') === state.tier;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function renderMoney() {
    var t = totals();
    var active = activeSteps();

    // The plain subtotal. There is no bundle discount to predict any more, and
    // the cart is the only thing that knows the final price anyway.
    var totalEls = [$('bottomBarTotalPrice'), $('rv2SummaryTotal')];
    totalEls.forEach(function (el) {
      if (!el) return;
      el.textContent = money(t.originalCents);
    });

    var productCount = active.reduce(function (n, s) { return n + activeSlots(s).length; }, 0);
    var countText = productCount + ' product' + (productCount === 1 ? '' : 's');

    // Same figure as the bar, at the top of the page, so nobody scrolls a
    // routine wondering what it costs.
    var hero = $('rv2HeroPrice');
    if (hero) {
      if (productCount) {
        hero.textContent = money(t.originalCents) + ' \u00b7 ' + countText;
        hero.hidden = false;
      } else {
        hero.hidden = true;
      }
    }
    [$('rv2BarCount'), $('rv2SummaryCount')].forEach(function (el) {
      if (el) el.textContent = countText;
    });

    document.querySelectorAll('[data-rv2-cta]').forEach(function (btn) {
      btn.disabled = active.length === 0;
    });

    // Desktop summary lines
    var lines = $('rv2SummaryLines');
    if (lines) {
      lines.innerHTML = active.map(function (s) {
        var multi = activeSlots(s).length > 1;
        return activeSlots(s).map(function (sl) {
          var p = slotProduct(sl);
          if (!p) return '';
          var img = p.image ? 'background-image:url(' + esc(p.image) + ')' : '';
          return '<div class="rv2-line">' +
            '<span class="rv2-line__img" style="' + img + '" role="img" aria-label=""></span>' +
            '<span class="rv2-line__body"><span class="rv2-line__name">' + esc(decode(p.name)) + '</span>' +
            '<span class="rv2-line__use">' + esc(multi ? sl.label : s.def.time) + '</span></span>' +
            '<span class="rv2-line__price">' + money(p.price) + '</span></div>';
        }).join('');
      }).join('') || '<p class="rv2-line__empty">Nothing selected yet.</p>';
    }

    // Persist for a refresh, keyed by STEP so a product shared by two steps
    // cannot be restored into both. See buildSteps() for why.
    var sel = {};
    active.forEach(function (s) {
      var bag = {};
      s.slots.forEach(function (sl) {
        var p = slotProduct(sl);
        if (p) bag[sl.key] = p.id;
      });
      sel[s.def.id] = bag;
    });
    writeJSON(STORAGE.SELECTED, sel);
  }

  function renderHeader() {
    var el = $('rv2StepsAway');
    if (!el) return;
    var name = (state.profile && state.profile.name) ? String(state.profile.name).trim().split(/\s+/)[0] : '';
    var n = state.steps.filter(stepInTier).length;
    el.textContent = name
      ? name + ', your routine is ' + n + ' steps.'
      : 'Your routine is ' + n + ' steps.';

    var chips = $('rv2WhyChips');
    if (chips) {
      var answers = [];
      if (state.profile.skinType) answers.push(state.profile.skinType + ' skin');
      if (state.profile.sensitivity === 'Sensitive') answers.push('Sensitive');
      (state.profile.concerns || []).forEach(function (c) { if (c) answers.push(c); });
      if (state.profile.isPregnant) answers.push('Pregnancy-safe');
      chips.innerHTML = answers.map(function (a) {
        return '<span class="rv2-chip">' + esc(a) + '</span>';
      }).join('');
    }
    var why = $('rv2WhyText');
    if (why) {
      var concern = (state.profile.concerns && state.profile.concerns[0]) || state.profile.primaryConcern;
      why.textContent = 'Chosen for ' + String(state.profile.skinType || '').toLowerCase() +
        ' skin' + (concern ? ' with ' + String(concern).toLowerCase() : '') +
        (state.profile.sensitivity === 'Sensitive' ? ', kept gentle because you told us your skin reacts easily' : '') + '.';
    }
  }

  function renderAll() {
    renderTiers();
    renderSteps();
    renderMoney();
    renderHeader();
  }

  // ---------------------------------------------------------------
  // Interaction
  // ---------------------------------------------------------------
  document.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target : null;
    if (!t) return;

    var opt = t.closest('.rv2-opt');
    if (opt) {
      var step = stepById(opt.getAttribute('data-step'));
      var slotKey = opt.getAttribute('data-slot');
      var slot = step && step.slots.filter(function (sl) { return sl.key === slotKey; })[0];
      if (step && slot) {
        slot.selectedId = opt.getAttribute('data-variant');
        renderSteps();
        renderMoney();
        var chosen = slotProduct(slot);
        if (chosen) announce(step.def.pill + ': ' + decode(chosen.name) + ' selected');
      }
      return;
    }

    // [data-action="tier-advanced"] was the "Add Advanced" button on the
    // out-of-tier placeholder, which is no longer rendered.
    var tierBtn = t.closest('.rv2-tier');
    if (tierBtn) {
      state.tier = tierBtn.getAttribute('data-tier') || 'advanced';
      try { localStorage.setItem(STORAGE.TIER, state.tier); } catch (err) {}
      renderAll();
      announce((state.tier === 'advanced' ? 'Advanced' : 'Essential') +
        ' routine, ' + activeSteps().length + ' steps');
      return;
    }

    var act = t.closest('[data-action]');
    if (!act) return;
    var action = act.getAttribute('data-action');
    var id = act.getAttribute('data-step');

    if (action === 'why') {
      var s1 = stepById(id);
      if (s1) { s1.whyOpen = !s1.whyOpen; renderSteps(); }
    } else if (action === 'remove') {
      state.pendingRemoveId = id;
      openLayer($('rv2Confirm'));
    } else if (action === 'addback') {
      var s2 = stepById(id);
      if (s2) {
        s2.removed = false; persistRemoved(); renderSteps(); renderMoney();
        announce(s2.def.pill + ' added back to your routine');
      }
    } else if (action === 'detail') {
      openDetail(id, act.getAttribute('data-slot'));
    } else if (action === 'one-cleanser' || action === 'two-cleansers') {
      state.oneCleanser = action === 'one-cleanser';
      writeJSON(STORAGE.ONECLEANSER, state.oneCleanser);
      renderSteps(); renderMoney();
      announce(state.oneCleanser
        ? oneCleanserAdvice()
        : 'Separate evening cleanser added back to your routine');
    }
  });

  // Which cleanser to keep flips with skin type, and the routine already knows
  // it - so this says something specific rather than a generic nudge. It is a
  // statement about the products, not advice about skin.
  function oneCleanserAdvice() {
    var oily = state.profile && state.profile.skinType === 'Oily';
    return oily
      ? 'Keeping your morning cleanser - it is light enough to use at both ends of the day.'
      : 'Keeping your morning cleanser. Your sunscreen still needs taking off at night, so use it then too.';
  }

  function persistRemoved() {
    writeJSON(STORAGE.REMOVED, state.steps.filter(function (s) { return s.removed; })
      .map(function (s) { return s.def.id; }));
  }

  // ---------------------------------------------------------------
  // Modal layers.
  //
  // Both the remove-confirm and the product sheet declare role="dialog" and
  // aria-modal="true", but opening them was only ever `el.hidden = false`. Focus
  // stayed on the button behind the scrim, so a screen reader announced nothing
  // and kept reading the page underneath, and Tab walked through the routine
  // behind an opaque full-screen sheet. These three functions are what makes
  // aria-modal true rather than merely claimed.
  // ---------------------------------------------------------------
  var layerStack = [];

  function focusables(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) { return !el.disabled && el.getClientRects().length; }
    );
  }

  function focusSafely(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  function openLayer(el) {
    if (!el || !el.hidden) return;
    var opener = document.activeElement;
    el.hidden = false;
    var f = focusables(el);
    focusSafely(f[0] || el);
    layerStack.push({ el: el, opener: opener });
  }

  function closeLayer(el, focusInstead) {
    if (!el || el.hidden) return;
    el.hidden = true;
    for (var i = layerStack.length - 1; i >= 0; i--) {
      if (layerStack[i].el !== el) continue;
      var rec = layerStack.splice(i, 1)[0];
      // The opener can be gone: confirming a removal re-renders the step list
      // and destroys the "Remove this step" button we came from. Callers pass
      // somewhere sensible to land instead.
      var target = focusInstead || rec.opener;
      if (target && document.contains(target)) focusSafely(target);
      break;
    }
  }

  function topLayer() {
    return layerStack.length ? layerStack[layerStack.length - 1].el : null;
  }

  function wireStatic() {
    var close = $('rv2Close');
    if (close) close.addEventListener('click', function () { window.location.href = '/'; });

    var whyBtn = $('rv2WhyBtn');
    if (whyBtn) whyBtn.addEventListener('click', function () {
      var panel = $('rv2WhyPanel');
      if (!panel) return;
      panel.hidden = !panel.hidden;
      whyBtn.setAttribute('aria-expanded', panel.hidden ? 'false' : 'true');
    });

    var go = $('rv2ConfirmGo');
    if (go) go.addEventListener('click', function () {
      var removedId = state.pendingRemoveId;
      var s = stepById(removedId);
      if (s) { s.removed = true; persistRemoved(); }
      state.pendingRemoveId = null;
      renderSteps(); renderMoney();
      announce(s ? s.def.pill + ' removed from your routine' : 'Step removed');
      // The button we opened from no longer exists, so land on the "Add back"
      // control that replaced it — the one thing the shopper might want next.
      var back = document.querySelector('[data-action="addback"][data-step="' + removedId + '"]');
      closeLayer($('rv2Confirm'), back);
    });
    var keep = $('rv2ConfirmKeep');
    if (keep) keep.addEventListener('click', function () {
      state.pendingRemoveId = null;
      closeLayer($('rv2Confirm'));
    });

    [$('rv2DetailClose'), $('rv2DetailKeep')].forEach(function (b) {
      if (b) b.addEventListener('click', function () { closeLayer($('rv2Detail')); });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var top = topLayer();
        if (top) { e.preventDefault(); closeLayer(top); }
        return;
      }
      // Keep Tab inside the open layer. Without this, tabbing out of a
      // full-screen sheet walks the routine behind it, invisibly.
      if (e.key !== 'Tab') return;
      var layer = topLayer();
      if (!layer) return;
      var f = focusables(layer);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); focusSafely(last); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); focusSafely(first); }
      else if (!layer.contains(document.activeElement)) { e.preventDefault(); focusSafely(first); }
    });

    document.querySelectorAll('[data-rv2-cta]').forEach(function (btn) {
      btn.addEventListener('click', addRoutineToCart);
    });
  }

  // ---------------------------------------------------------------
  // Product detail — long copy, ingredients and how-to-use are fetched on
  // demand so they never ride along in the page payload.
  // ---------------------------------------------------------------
  function openDetail(stepId, slotKey) {
    var step = stepById(stepId);
    if (!step) return;
    var slot = slotKey && step.slots.filter(function (sl) { return sl.key === slotKey; })[0];
    var p = slot ? slotProduct(slot) : selectedProduct(step);
    if (!p) return;

    state.detailStepId = stepId;
    $('rv2DetailStep').textContent = step.def.pill + ' · ' + categoryFor(step.def, state.profile);
    $('rv2DetailName').textContent = decode(p.name);
    $('rv2DetailMedia').style.backgroundImage = p.image ? 'url(' + p.image + ')' : '';
    $('rv2DetailMeta').innerHTML =
      '<span class="rv2-detail__price">' + money(p.price) + '</span>' +
      (p.size ? '<span class="rv2-detail__size">' + esc(p.size) + '</span>' : '') +
      '<span class="rv2-detail__time">' + esc(step.def.time) + '</span>' +
      ratingRow(p);
    $('rv2DetailLong').textContent = decode(p.description || '');
    $('rv2DetailWhy').textContent = step.def.why;
    $('rv2DetailWhyWrap').hidden = false;
    openLayer($('rv2Detail'));

    if (!p.handle) return;
    fetch('/products/' + encodeURIComponent(p.handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (full) {
        if (state.detailStepId !== stepId) return;   // they moved on
        if (full.description) {
          $('rv2DetailLong').textContent = decode(String(full.description).replace(/<[^>]*>/g, ' ')).replace(/\s+/g, ' ').trim();
        }
      })
      .catch(function () { /* the sheet is already usable without this */ });
  }

  // ---------------------------------------------------------------
  // Cart. Same contract as before: add, apply the code through the cart so we
  // learn what Shopify actually did, then on to checkout.
  // ---------------------------------------------------------------
  function addRoutineToCart(e) {
    var btn = e && e.currentTarget;
    var active = activeSteps();
    if (!active.length) return;

    var wanted = [];
    active.forEach(function (s) {
      activeSlots(s).forEach(function (sl) {
        var p = slotProduct(sl);
        var id = p && parseInt(p.id, 10);
        if (id > 0) wanted.push(id);
      });
    });

    var labels = [];
    document.querySelectorAll('[data-rv2-cta]').forEach(function (b) {
      labels.push(b.textContent);
      b.disabled = true;
      b.textContent = 'Adding…';
    });

    // Read the cart BEFORE adding. /cart/add.js increments an existing line
    // rather than replacing it, so a shopper who already had the cleanser — or
    // who simply pressed the button twice, or came back to this page later —
    // ended up with two of it and a total that no longer matched the page.
    // Anything already in the cart is left exactly as it is, quantity included:
    // if they deliberately put three of something in, that is their business.
    fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (cart) {
        var already = {};
        // A null cart means the read failed. Adding everything is the safer
        // failure: a duplicate line is recoverable, a missing product is a
        // routine the shopper paid for and did not receive.
        if (cart && cart.items) {
          cart.items.forEach(function (li) { already[li.id] = true; });
        }

        var items = [];
        var seen = {};
        wanted.forEach(function (id) {
          if (seen[id]) return;        // one product can answer two steps
          seen[id] = true;
          if (already[id]) return;     // already in the cart — leave it alone
          items.push({ id: id, quantity: 1 });
        });

        if (!items.length) return null;   // everything is already in the cart

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        }).then(function (r) {
          return r.json().then(function (b) { return { ok: r.ok, body: b }; });
        }).then(function (res) {
          if (!res.ok) throw new Error((res.body && (res.body.description || res.body.message)) || 'Could not add to cart');
          return res;
        });
      })
      .then(function () { window.location.href = '/checkout'; })
      .catch(function (err) {
        document.querySelectorAll('[data-rv2-cta]').forEach(function (b, i) {
          b.disabled = false;
          b.textContent = labels[i] || 'Complete my routine';
        });
        var line = $('bundleOfferLine');
        if (line) {
          line.textContent = /sold out|unavailable|out of stock/i.test(err.message || '')
            ? 'Something just went out of stock — please pick another option for that step.'
            : 'Something went wrong adding your routine. Please try again.';
          line.hidden = false;
        }
      });
  }

  // ---------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------
  function boot() {
    var dataEl = $('routineProductsData');
    if (!dataEl) { hideLoader(); return; }

    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { hideLoader(); return; }
    if (!data || !data.routines || !data.routines.length) { hideLoader(); return; }

    state.profile = loadProfile();
    // No profile means there is nothing to personalise, and showing a stranger's
    // routine under "your routine" would be a lie. Send them to the quiz.
    if (!state.profile) {
      window.location.replace('/pages/skin-type-quiz');
      return;
    }

    state.routine = pickRoutine(data.routines, state.profile);
    if (!state.routine) { hideLoader(); return; }

    buildSteps();
    wireStatic();
    renderAll();
    trackBarHeight();
    hideLoader();
  }

  // The loading state is markup that ships visible, so it covers the empty
  // routine from the first paint rather than appearing after a flash of it.
  // Anything that ends the boot path has to clear it.
  function hideLoader() {
    var el = $('rv2Loading');
    if (el) el.hidden = true;
  }

  // The fixed bar is not a fixed HEIGHT: the status line appears, disappears and
  // wraps to two lines on narrow phones, and iOS adds a safe-area inset. The
  // scroll region reserves --rv2-bar-h at the bottom, so that number has to
  // follow the bar or the last card ends up underneath it again.
  function trackBarHeight() {
    var bar = $('resultsBottomBar');
    var overlay = document.getElementById('routineBuilderOverlay') || document.documentElement;
    if (!bar) return;

    var apply = function () {
      // Desktop hides the bar; a measured 0 would pull the padding out from
      // under the desktop rule, so leave the stylesheet's value alone there.
      var h = bar.getBoundingClientRect().height;
      if (h > 0) overlay.style.setProperty('--rv2-bar-h', Math.ceil(h) + 'px');
      else overlay.style.removeProperty('--rv2-bar-h');
    };

    apply();
    if (window.ResizeObserver) {
      new ResizeObserver(apply).observe(bar);
    } else {
      window.addEventListener('resize', apply);
    }
  }

  // Backstop: if boot() throws before reaching hideLoader(), the shopper would
  // be left staring at a spinner over a page that is otherwise fine. Better to
  // uncover a broken routine than to hide a working one behind a permanent
  // loading state.
  window.setTimeout(hideLoader, 8000);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
